import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, LinearProgress,
  Snackbar, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import PaymentRequestDocuments from "../contract/legal/payments/PaymentRequestDocuments";
import PaymentFlowTrail from "../contract/legal/payments/PaymentFlowTrail";
import PaymentDocumentsCard from "../contract/legal/payments/PaymentDocumentsCard";
import {
  advancePaymentRequest,
  getFinancialPaymentRequests,
  paymentFlowErrorMessage,
  receivePaymentRequest,
  returnPaymentRequestToCausation,
} from "../../services/backend/PaymentRequestLegalService";
import { getContractConfig } from "../../services/backend/ContractConfigService";
import { UploadFile } from "../../services/backend/UploadFileService";
import { normalizeNit } from "../entities/entityLogic";
import { PAYMENT_REQUEST_STATUS, PAYMENT_REQUEST_STATUS_UI, PERMISSIONS } from "../../constants";
import {
  RETURN_TARGET_STATUS,
  getStageActionBlockReason,
  getStageOf,
  isoToday,
  resolvePaymentRequestDocs,
} from "../contract/legal/payments/paymentRequestLogic";

// Detalle de una solicitud en Gestión de Pagos (legal-payment-financial-flow).
// Reúne lo que el acta #3 pidió ver al gestionar un pago —contrato, ejecución,
// datos de la solicitud, recorrido— y el panel de acciones de la etapa del
// actor. Cada etapa es un CHECK (daily 2026-09-14): avanzar no exige datos;
// Tesorería puede registrar fecha y referencia, Caja un comprobante, ambos
// opcionales. Devolver exige motivo y solo desde Contabilidad, Presupuesto y
// Tesorería, siempre a Causación.
//
// Todo motivo de bloqueo sale de getStageActionBlockReason, la MISMA regla que
// aplica el servidor: aquí solo se deshabilita el botón y se explica por qué.

const BUCKET = process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS;

const currency = (value) =>
  "$" + (Number(value) || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 });
const percent = (rate) => `${Math.round((Number(rate) || 0) * 100)}%`;
const formatCalendar = (iso) => {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const extensionOf = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "pdf";
};

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.25 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ textAlign: "right" }}>{value}</Typography>
  </Box>
);

const PaymentManagementDetail = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);

  const [row, setRow] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind: 'advance' | 'return', paidAt, reference, proof, message }
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const notify = useCallback(
    (message, severity = "error") => setSnack({ open: true, message, severity }),
    []
  );

  const permissions = user?.permissions || [];
  const isAdmin = !!user?.roles?.includes("ADMIN");

  useEffect(() => {
    if (!isAdmin && !permissions.includes(PERMISSIONS.PAYMENT_FLOW.LIST)) {
      navigate("/");
    }
  }, [isAdmin, permissions, navigate]);

  useEffect(() => {
    let active = true;
    (async () => {
      const [result, config] = await Promise.all([
        getFinancialPaymentRequests(user, { requestId, includePaid: true }),
        getContractConfig(),
      ]);
      if (!active) return;
      const found = result.ok ? (result.data || [])[0] : null;
      if (!found) {
        notify("No fue posible cargar la solicitud");
        navigate("/payments/management");
        return;
      }
      setRow(found);
      setCatalog(resolvePaymentRequestDocs(config?.paymentRequestDocs));
      setLoading(false);
    })();
    return () => { active = false; };
  }, [requestId]); // eslint-disable-line

  const stage = useMemo(() => (row ? getStageOf(row.status) : null), [row]);
  const advanceAction = stage?.advanceAction || "ADVANCE";

  // Motivos de bloqueo de los dos botones. Para el de devolver se pasa un motivo
  // ficticio: lo que se quiere saber aquí es si el actor PUEDE devolver; el
  // motivo real se exige en el diálogo.
  const advanceReason = useMemo(
    () => (row ? getStageActionBlockReason({ request: row, action: advanceAction, actorPermissions: permissions, isAdmin }) : ""),
    [row, advanceAction, permissions, isAdmin]
  );
  const returnReason = useMemo(
    () => (row ? getStageActionBlockReason({ request: row, action: "RETURN", actorPermissions: permissions, isAdmin, payload: { message: "·" } }) : ""),
    [row, permissions, isAdmin]
  );

  const applyResult = (result, successMessage) => {
    if (!result.ok) {
      notify(paymentFlowErrorMessage(result));
      return false;
    }
    // El servidor devuelve la solicitud sin el enriquecimiento de la bandeja:
    // se conserva lo del contrato y se pisa lo de la solicitud.
    setRow((prev) => ({ ...prev, ...result.data, lastMovementAt: result.data?.financial?.lastMovementAt || prev?.lastMovementAt }));
    notify(successMessage, "success");
    return true;
  };

  const handleAdvance = async () => {
    if (!row || !stage || busy) return;
    const payload = {};
    if (stage.optional.includes("paidAt") && dialog?.paidAt) payload.paidAt = dialog.paidAt;
    if (stage.optional.includes("reference") && dialog?.reference?.trim()) payload.reference = dialog.reference.trim();
    if (stage.optional.includes("proof") && dialog?.proof) payload.proof = dialog.proof;
    const reason = getStageActionBlockReason({ request: row, action: advanceAction, actorPermissions: permissions, isAdmin, payload });
    if (reason) { notify(reason); return; }

    setBusy(true);
    const result = advanceAction === "RECEIVE"
      ? await receivePaymentRequest(row.requestId, user)
      : await advancePaymentRequest(row.requestId, payload, user);
    setBusy(false);
    const toLabel = PAYMENT_REQUEST_STATUS_UI[stage.advanceTo]?.label || stage.advanceTo;
    if (applyResult(result, `La solicitud pasó a «${toLabel}»`)) setDialog(null);
  };

  const handleReturn = async () => {
    if (!row || busy) return;
    const message = (dialog?.message || "").trim();
    const reason = getStageActionBlockReason({ request: row, action: "RETURN", actorPermissions: permissions, isAdmin, payload: { message } });
    if (reason) { notify(reason); return; }

    setBusy(true);
    const result = await returnPaymentRequestToCausation(row.requestId, message, user);
    setBusy(false);
    if (applyResult(result, "La solicitud fue devuelta a Causación")) setDialog(null);
  };

  // Comprobante opcional de Caja: misma ruta de carga que los documentos de la
  // solicitud, bajo la carpeta de la empresa.
  const handleProofFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !row) return;
    setBusy(true);
    const path = `entities/${normalizeNit(row.entityNit)}/paymentRequests/${row.contractCode}/${row.requestId}/proof_${Date.now()}.${extensionOf(file.name)}`;
    const response = await UploadFile(user, file, BUCKET, path);
    setBusy(false);
    if (!response) { notify("No fue posible cargar el comprobante"); return; }
    setDialog((d) => ({
      ...d,
      proof: { url: response.downloadUrl, name: file.name, size: file.size, type: file.type || "", path },
    }));
  };

  if (loading || !row) {
    return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>;
  }

  const statusUi = PAYMENT_REQUEST_STATUS_UI[row.status] || {};
  const execution = row.execution || null;
  const isPaid = row.status === PAYMENT_REQUEST_STATUS.PAID;
  const toLabel = stage ? PAYMENT_REQUEST_STATUS_UI[stage.advanceTo]?.label || stage.advanceTo : "";
  const returnLabel = PAYMENT_REQUEST_STATUS_UI[RETURN_TARGET_STATUS]?.label || "Causación";
  const canAct = !!stage && (isAdmin || permissions.includes(stage.permission));

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/payments/management")}>Gestión de Pagos</Button>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h5">Solicitud de pago #{row.seq} · Contrato {row.contractCode}</Typography>
        <Chip
          label={statusUi.label || row.status}
          color={statusUi.color}
          sx={statusUi.sx}
          icon={statusUi.Icon ? <statusUi.Icon /> : undefined}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {row.businessName || "—"}{row.entityNit ? ` · NIT ${row.entityNit}` : ""}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>Contrato</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{row.contractSubject || "—"}</Typography>
              {execution && (
                <>
                  <Row label="Valor vigente" value={currency(execution.budget)} />
                  <Row label="Causado (aprobado y posterior)" value={currency(execution.approved)} />
                  <Row label="Pagado" value={currency(execution.paid)} />
                  <Row label="En revisión del supervisor" value={currency(execution.inReview)} />
                  <Row label="Saldo" value={currency(execution.balance)} />
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Ejecución financiera</Typography>
                      <Typography variant="caption">{percent(execution.executionRate)}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(100, execution.executionRate * 100)} />
                  </Box>
                </>
              )}
              {row.contractFinishedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Fin del contrato: {formatCalendar(String(row.contractFinishedAt).slice(0, 10))}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Factura</Typography>
              <Row label="Número" value={row.invoiceNumber || "—"} />
              <Row label="Valor facturado" value={row.invoiceAmount ? currency(row.invoiceAmount) : "—"} />
              <Row label="Fecha de emisión" value={formatCalendar(row.invoiceIssuedAt)} />
              <Row label="Fecha de vencimiento" value={formatCalendar(row.invoiceDueAt)} />
              {row.approvedByName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Aprobada por supervisión: {row.approvedByName}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Panel de acciones de la etapa. Solo pinta botones para quien
              porta el permiso de la etapa ACTUAL; los demás ven el recorrido. */}
          {stage && canAct && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>{stage.label}</Typography>
                <Stack spacing={1}>
                  <Tooltip title={advanceReason}>
                    <span>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={busy ? <CircularProgress size={16} /> : <ArrowForwardIcon />}
                        disabled={busy || !!advanceReason}
                        onClick={() => setDialog({ kind: "advance", paidAt: "", reference: "", proof: null })}
                      >
                        {stage.advanceLabel}
                      </Button>
                    </span>
                  </Tooltip>
                  {stage.canReturn && (
                    <Tooltip title={returnReason}>
                      <span>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="warning"
                          startIcon={<AssignmentReturnIcon />}
                          disabled={busy || !!returnReason}
                          onClick={() => setDialog({ kind: "return", message: "" })}
                        >
                          Devolver a Causación
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {advanceReason && (
                    <Typography variant="caption" color="text.secondary">{advanceReason}</Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
          {stage && !canAct && (
            <Alert severity="info" sx={{ mt: 2 }}>
              La solicitud está en {stage.label}. Solo esa área puede actuar sobre ella.
            </Alert>
          )}
          {isPaid && (
            <Alert severity="success" sx={{ mt: 2 }}>Pago registrado. El recorrido de esta factura terminó.</Alert>
          )}
        </Grid>

        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recorrido</Typography>
              <PaymentFlowTrail request={row} />
            </CardContent>
          </Card>
          {/* Financiera solo descarga los documentos; regenerarlos es del
              supervisor (o ADMIN, para soporte). */}
          <PaymentDocumentsCard
            request={row}
            canRegenerate={isAdmin}
            onUpdated={(data) => setRow((prev) => ({ ...prev, ...data }))}
            onError={notify}
          />
          <PaymentRequestDocuments
            request={row}
            catalog={catalog}
            rutDocument={row.rutDocument || null}
            readOnly
            onError={notify}
            canReview={false}
            canUpdateEntityDocs={false}
          />
        </Grid>
      </Grid>

      {/* Avance: nombra el estado destino como en el acta. Tesorería y Caja
          ven sus campos opcionales. */}
      <Dialog open={dialog?.kind === "advance"} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{stage?.advanceLabel}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La solicitud #{row.seq} del contrato {row.contractCode} pasará a «{toLabel}».
          </DialogContentText>
          {stage?.optional.includes("paidAt") && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Fecha del pago en banco (opcional)"
                type="date"
                value={dialog?.paidAt || ""}
                inputProps={{ max: isoToday() }}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setDialog((d) => ({ ...d, paidAt: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Referencia del pago (opcional)"
                value={dialog?.reference || ""}
                onChange={(e) => setDialog((d) => ({ ...d, reference: e.target.value }))}
                fullWidth
              />
            </Stack>
          )}
          {stage?.optional.includes("proof") && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
                {dialog?.proof ? "Cambiar comprobante" : "Adjuntar comprobante (opcional)"}
                <input hidden type="file" accept="application/pdf,image/*" onChange={handleProofFile} />
              </Button>
              {dialog?.proof && (
                <Typography variant="caption" color="text.secondary">{dialog.proof.name}</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdvance} disabled={busy}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Devolución: motivo obligatorio, destino siempre Causación. */}
      <Dialog open={dialog?.kind === "return"} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Devolver a Causación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La solicitud volverá a «{returnLabel}» y las etapas intermedias no la verán. Escribe el motivo:
            Causación lo necesita para corregir y volver a impulsarla.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            label="Motivo de la devolución"
            value={dialog?.message || ""}
            onChange={(e) => setDialog((d) => ({ ...d, message: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleReturn}
            disabled={busy || !(dialog?.message || "").trim()}
          >
            Devolver
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentManagementDetail;
